import { forwardRef } from 'react'

import { tv } from 'tailwind-variants'

export type TRadioSize = 'sm' | 'md' | 'lg'

export interface IRadioOption {
  text: string
  value: string
  disabled?: boolean
}

export type TRadioGroupOptions = [IRadioOption, IRadioOption, ...IRadioOption[]]

export type TRadioGroupProps = {
  options: TRadioGroupOptions
  size?: TRadioSize
  ariaLabel?: string
  ariaDescribedby?: string
  required?: boolean
} & Pick<
  React.InputHTMLAttributes<HTMLInputElement>,
  'name' | 'value' | 'checked' | 'onChange' | 'onFocus' | 'onBlur' | 'disabled'
>

export const radioVariants = tv({
  slots: {
    labelSlot: 'flex min-w-0 items-center gap-2',
    radioSlot:
      'bg-surface-white-50 flex flex-shrink-0 items-center justify-center rounded-full border-2 transition-colors',
    dotSlot: 'bg-surface-black-50 flex-shrink-0 rounded-full',
    textSlot: 'min-w-0'
  },
  variants: {
    size: {
      sm: {
        radioSlot: 'h-4 min-h-[1rem] w-4 min-w-[1rem]',
        dotSlot: 'h-1.5 min-h-[0.375rem] w-1.5 min-w-[0.375rem]',
        textSlot: 'text-caption-md'
      },
      md: {
        radioSlot: 'h-5 min-h-[1.25rem] w-5 min-w-[1.25rem]',
        dotSlot: 'h-2 min-h-[0.5rem] w-2 min-w-[0.5rem]',
        textSlot: 'text-body-md'
      },
      lg: {
        radioSlot: 'h-6 min-h-[1.5rem] w-6 min-w-[1.5rem]',
        dotSlot: 'h-2.5 min-h-[0.625rem] w-2.5 min-w-[0.625rem]',
        textSlot: 'text-body-lg'
      }
    },
    checked: {
      true: {
        radioSlot: 'border-surface-black-50',
        labelSlot: 'text-surface-black-50',
        textSlot: 'font-bold'
      },
      false: {
        radioSlot: 'border-surface-white-400',
        labelSlot: 'text-surface-black-50',
        textSlot: 'font-normal'
      }
    },
    disabled: {
      true: {
        labelSlot: 'text-surface-white-400 cursor-not-allowed',
        radioSlot: 'border-surface-white-400',
        dotSlot: 'bg-surface-white-400'
      },
      false: { labelSlot: 'cursor-pointer' }
    }
  },
  compoundVariants: [
    { size: 'sm', checked: true, class: { textSlot: 'text-caption-md' } },
    { size: 'md', checked: true, class: { textSlot: 'text-body-md' } },
    { size: 'lg', checked: true, class: { textSlot: 'text-body-lg' } },
    { size: 'sm', checked: false, class: { textSlot: 'text-caption-md' } },
    { size: 'md', checked: false, class: { textSlot: 'text-body-md' } },
    { size: 'lg', checked: false, class: { textSlot: 'text-body-lg' } },
    { size: 'sm', disabled: true, class: { textSlot: 'text-caption-md' } },
    { size: 'md', disabled: true, class: { textSlot: 'text-body-md' } },
    { size: 'lg', disabled: true, class: { textSlot: 'text-body-lg' } },
    {
      checked: true,
      disabled: true,
      class: { labelSlot: 'text-surface-white-400' }
    },
    {
      checked: false,
      disabled: true,
      class: { labelSlot: 'text-surface-black-500' }
    }
  ],
  defaultVariants: { size: 'md', checked: false, disabled: false }
})

const RadioIcon = ({
  checked,
  className
}: {
  checked: boolean
  className: string
}) => (checked ? <span className={className} /> : null)

type TRadioProps = Pick<
  React.InputHTMLAttributes<HTMLInputElement>,
  'name' | 'value' | 'checked' | 'onChange' | 'onFocus' | 'onBlur' | 'disabled'
> & {
  option: IRadioOption
  size: TRadioSize
}

export const Radio = ({
  option,
  checked,
  size,
  name,
  onChange,
  onBlur,
  onFocus
}: TRadioProps): JSX.Element => {
  const { labelSlot, radioSlot, dotSlot, textSlot } = radioVariants({
    size,
    checked,
    disabled: option.disabled
  })

  const inputId = `${name}-${option.value}`

  return (
    <label htmlFor={inputId} className={labelSlot()}>
      <input
        type='radio'
        id={inputId}
        name={name}
        value={option.value}
        checked={checked}
        onChange={onChange}
        onBlur={onBlur}
        onFocus={onFocus}
        disabled={option.disabled}
        readOnly={!onChange}
        className='sr-only'
      />
      <span className={radioSlot()}>
        <RadioIcon checked={!!checked} className={dotSlot()} />
      </span>
      <span className={textSlot()}>{option.text}</span>
    </label>
  )
}

export const RadioGroup = forwardRef<HTMLDivElement, TRadioGroupProps>(
  (
    {
      options,
      value,
      onChange,
      size = 'md',
      name,
      onBlur,
      onFocus,
      ariaLabel,
      ariaDescribedby,
      required = false
    }: TRadioGroupProps,
    ref
  ) => (
    <div
      ref={ref}
      className='flex flex-col gap-4'
      role='radiogroup'
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedby}
      aria-required={required}
    >
      {options.map(option => (
        <Radio
          key={option.value}
          option={option}
          checked={value === option.value}
          size={size}
          name={name}
          onChange={onChange}
          onBlur={onBlur}
          onFocus={onFocus}
        />
      ))}
    </div>
  )
)

RadioGroup.displayName = 'RadioGroup'
